import React, { useEffect, useState } from 'react';
import { ImageIcon, ImagePlus, Star, Trash, ArrowUp, AlertCircle, X } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface ProductImage {
  id: number;
  url: string;
  principal: boolean;
  orden: number;
  producto_variante_id: number;
  variante_id?: string;
}

interface VariantImageManagerProps {
  variantId: string;
  images: ProductImage[];
  uploadingImages: Record<string, boolean>;
  imageError: string | null;
  onUpload: (variantId: string, file: File) => void;
  onDelete: (variantId: string, imageId: number) => void;
  onSetPrincipal: (variantId: string, imageId: number) => void;
  onReorder?: (variantId: string, imageId: number, direction: 'up' | 'down') => void;
  showHeader?: boolean;
}

const VariantImageManager: React.FC<VariantImageManagerProps> = ({
  variantId,
  images,
  uploadingImages,
  imageError,
  onUpload,
  onDelete,
  onSetPrincipal,
  onReorder,
  showHeader = true
}) => {
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLastImageWarning, setShowLastImageWarning] = useState(false);
  
  // Effect to refresh signed URLs before they expire
  useEffect(() => {
    // Set a timer to refresh URLs after 50 minutes (before 60-min expiration)
    const refreshTimer = setTimeout(() => {
      setLastRefreshed(new Date()); // This will trigger the URL generation effect
    }, 50 * 60 * 1000); // 50 minutes
    
    return () => clearTimeout(refreshTimer);
  }, [lastRefreshed]);

  // Effect to generate signed URLs for the images
  useEffect(() => {
    const getSignedUrls = async () => {
      if (!images || images.length === 0) return;
      
      const newSignedUrls: Record<number, string> = {};
      
      // First, filter out full-size duplicates to only keep optimized versions
      // We'll create a map of filenames to their image objects, preferring optimized versions
      const uniqueImages = new Map<string, ProductImage>();
      
      // Process images to identify duplicates based on filename
      for (const img of images) {
        // Skip processing if the URL is empty
        if (!img.url) continue;
        
        // For principal images, always keep them regardless
        if (img.principal) {
          uniqueImages.set(`principal-${img.id}`, img);
          continue;
        }
        
        // Extract the filename from the URL
        let filename = img.url;
        
        // Handle full URLs
        if (img.url.startsWith('http')) {
          const urlParts = img.url.split('/');
          filename = urlParts[urlParts.length - 1];
        }
        
        // Check if this is a full or optimized version
        const isFullVersion = img.url.includes('/full/');
        const isOptVersion = img.url.includes('/opt/');
        
        // If we already have this filename and current is full version, skip it
        // We prefer optimized versions over full versions
        if (uniqueImages.has(filename)) {
          const existingImg = uniqueImages.get(filename)!;
          const existingIsFullVersion = existingImg.url.includes('/full/');
          
          // Replace only if current is optimized and existing is full version
          if (isOptVersion && existingIsFullVersion) {
            uniqueImages.set(filename, img);
          }
        } else {
          // First time seeing this filename, add it
          uniqueImages.set(filename, img);
        }
      }
      
      // Now process only the unique images
      for (const img of uniqueImages.values()) {
        try {
          // If we already have a valid signed URL for this image, skip processing
          if (signedUrls[img.id] && !signedUrls[img.id].includes('undefined')) {
            newSignedUrls[img.id] = signedUrls[img.id];
            continue;
          }

          // Just use the original URL if it doesn't need signing or for any reason can't be signed
          let imageUrl = img.url;
          
          try {
            // Get user company for storage bucket
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('No user found');
      
            const { data: profile } = await supabase
              .from('profiles')
              .select('id_company, company_id')
              .eq('id', user.id)
              .single();
              
            let idCompany = (profile as any)?.id_company as string | null;
            if (!idCompany && (profile as any)?.company_id) {
              const { data: comp } = await supabase
                .from('company_profiles')
                .select('id_company')
                .eq('id', (profile as any).company_id)
                .single();
              idCompany = (comp as any)?.id_company ?? null;
            }
            if (!idCompany) throw new Error('No company associated with user');
            
            // Determine variant id (as string UUID) and filename
            const imgVariantId = (img.variante_id || (img.producto_variante_id ? String(img.producto_variante_id) : variantId)) as string;
            let filename = img.url;
            if (img.url.startsWith('http')) {
              // Legacy full URL: try to extract filename
              const urlParts = img.url.split('/');
              filename = urlParts[urlParts.length - 1];
            } else if (img.url.includes('/')) {
              // If the URL contains a path, take last segment as filename
              const parts = img.url.split('/');
              filename = parts[parts.length - 1];
            }

            // Build candidate paths (opt first, then full)
            const optPath = `${idCompany}/${imgVariantId}/opt/${filename}`;
            const fullPath = `${idCompany}/${imgVariantId}/full/${filename}`;

            // Try signing optimized version
            let signed: string | null = null;
            {
              const { data: optSigned } = await supabase.storage.from('productos').createSignedUrl(optPath, 3600);
              if (optSigned?.signedUrl) signed = optSigned.signedUrl;
            }
            // Fallback to full if opt missing
            if (!signed) {
              const { data: fullSigned } = await supabase.storage.from('productos').createSignedUrl(fullPath, 3600);
              if (fullSigned?.signedUrl) signed = fullSigned.signedUrl;
            }
            // No legacy fallback

            if (signed) {
              imageUrl = signed;
            } else if (img.url.startsWith('http')) {
              // As último recurso, mantener la URL original si ya es http
              imageUrl = img.url;
            } else {
              // No se pudo firmar y no es http: dejar filename para que onError del <img> lo gestione
              console.warn('No se pudo firmar la imagen, filename:', filename);
            }
          } catch (err) {
            console.error('Error creating signed URL:', err);
            // Keep the original URL if signing fails
          }
          
          newSignedUrls[img.id] = imageUrl;
        } catch (error) {
          console.error('Error processing image URL:', error);
          newSignedUrls[img.id] = img.url;
        }
      }
      
      setSignedUrls(newSignedUrls);
    };
    
    getSignedUrls();
  }, [images, lastRefreshed]);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(variantId, e.target.files[0]);
      e.target.value = ''; // Reset input
    }
  };

  return (
    <div className="mt-4">
      {showHeader && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Imágenes
        </label>
      )}
      
      {/* Image upload */}
      <div className="flex items-center mb-3">
        <input
          type="file"
          id={`image-upload-${variantId}`}
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
        />
        <label 
          htmlFor={`image-upload-${variantId}`}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-normal rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
        >
          <ImagePlus size={16} className="mr-1" />
          Agregar otra imagen
        </label>
        {uploadingImages[variantId] && (
          <span className="ml-2 text-sm text-gray-500">Subiendo imagen...</span>
        )}
      </div>
      
      {/* Error message */}
      {imageError && (
        <div className="mb-3 text-sm text-red-600">{imageError}</div>
      )}
      
      {/* Display existing images */}
      {images && images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {images.map((image, imgIndex) => (
            <div key={image.id} className="relative border border-gray-200 rounded-2xl overflow-hidden">
              <div className="relative">
                <img 
                  src={signedUrls[image.id] || image.url} 
                  alt="Imagen de producto"
                  className="w-full h-32 object-cover"
                  onError={(e) => {
                    console.error('Image load error:', image.url);
                    // Fall back to a placeholder if image fails to load
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Error';
                  }}
                />
                {image.principal && (
                  <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-md">
                    Principal
                  </div>
                )}
              </div>
              <div className="p-2 bg-white flex justify-between">
                <div className="flex space-x-2">
                  {!image.principal && (
                    <button
                      type="button"
                      onClick={() => onSetPrincipal(variantId, image.id)}
                      title="Establecer como principal"
                      className="text-blue-500 hover:text-blue-600"
                    >
                      <Star size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (images.length <= 1) {
                        setShowLastImageWarning(true);
                      } else {
                        setConfirmDeleteId(image.id);
                      }
                    }}
                    title="Eliminar imagen"
                    className="text-red-600 hover:text-red-700"
                    disabled={isDeleting}
                  >
                    <Trash size={16} />
                  </button>
                </div>
                {onReorder && (
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => onReorder(variantId, image.id, 'up')}
                      disabled={imgIndex === 0}
                      className={`text-gray-500 ${imgIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:text-gray-700'}`}
                      title="Mover hacia arriba"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onReorder(variantId, image.id, 'down')}
                      disabled={imgIndex === images.length - 1}
                      className={`text-gray-500 ${imgIndex === images.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:text-gray-700'}`}
                      title="Mover hacia abajo"
                    >
                      <ArrowUp className="transform rotate-180" size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-4 border border-dashed border-gray-300 rounded-2xl text-gray-500">
          <ImageIcon size={24} className="mb-2" />
          <p className="text-sm">No hay imágenes para esta variante</p>
        </div>
      )}
      
      {/* Last Image Warning */}
      {showLastImageWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4 text-amber-600">
              <AlertCircle className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-medium">No se puede eliminar</h3>
              <button 
                onClick={() => setShowLastImageWarning(false)} 
                className="ml-auto text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="mb-4">El producto debe tener al menos una imagen. No se puede eliminar la última imagen.</p>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowLastImageWarning(false)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Image Deletion */}
      {confirmDeleteId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4 text-red-600">
              <AlertCircle className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-medium">Confirmar eliminación</h3>
              <button 
                onClick={() => setConfirmDeleteId(null)} 
                className="ml-auto text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="mb-4">¿Estás seguro que quieres eliminar esta imagen?</p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                disabled={isDeleting}
              >
                No
              </button>
              <button
                onClick={async () => {
                  if (confirmDeleteId) {
                    setIsDeleting(true);
                    try {
                      await onDelete(variantId, confirmDeleteId);
                    } finally {
                      setIsDeleting(false);
                      setConfirmDeleteId(null);
                    }
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantImageManager;
